import { lazy, Suspense, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAttributeDefinitions } from "@/api/metadata/useAttributeDefinitions"
import { useProjectMutations } from "@/api/projects/useProjectMutations"
import { useStatusScheme } from "@/api/statusSchemes/useStatusScheme"
import { useVisibleAttributeFields } from "@/api/metadata/useVisibleAttributeFields"
import { useWorkspace } from "@/workspaces/WorkspaceContext"
import { AttributeFieldChip } from "@/features/attributes/attribute-field-chip"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon, Cancel01Icon } from "@hugeicons/core-free-icons"
import {
  MilestonesTable,
  type MilestoneDraft,
} from "@/features/project-page/milestones-table"
import { useMilestoneMutations } from "@/api/milestones/useMilestoneMutations"
import { parseInputDateValue, toInputDateValue } from "@/features/project-page/utils"
import type { CellSelectOption } from "@/components/cell-controls/cell-controls"
import { STATE_OPTIONS } from "@/features/project-page/types"

const RichTextMarkdownEditor = lazy(() =>
  import("@/components/ui/rich-text-markdown-editor").then((module) => ({
    default: module.RichTextMarkdownEditor,
  })),
)

const PROJECT_FIELDS_RAIL_SURFACE = "project_fields_rail"

const STATE_COLOR_HEX: Record<string, string> = {
  "bg-emerald-500": "#10b981",
  "bg-amber-500": "#f59e0b",
  "bg-rose-500": "#f43f5e",
  "bg-slate-400": "#94a3b8",
  "bg-sky-500": "#0ea5e9",
}

type PendingMilestone = {
  localId: string
  name: string
  date: string
}

let localMilestoneCounter = 0

export function CreateProjectDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  /** Called with the newly-created project id after successful creation. */
  onSuccess?: (projectId: string) => void
}) {
  const navigate = useNavigate()
  const { path, workspace } = useWorkspace()
  const { createProject } = useProjectMutations()
  const { statuses: projectStatuses } = useStatusScheme("project")
  const { createMilestone } = useMilestoneMutations()

  const { data: projectAttributes = [] } = useAttributeDefinitions("project")
  const { fields: visibleFields } = useVisibleAttributeFields(
    "project",
    PROJECT_FIELDS_RAIL_SURFACE,
  )

  const [name, setName] = useState("")
  const [summary, setSummary] = useState("")
  const [description, setDescription] = useState("")
  const [customAttributes, setCustomAttributes] = useState<Record<string, unknown>>({})
  const [pendingMilestones, setPendingMilestones] = useState<PendingMilestone[]>([])
  const nameRef = useRef<HTMLInputElement>(null)

  const stateOptions = useMemo<CellSelectOption[]>(
    () =>
      STATE_OPTIONS.map((option) => {
        const color = STATE_COLOR_HEX[option.color]
        return { value: option.value, label: option.label, color }
      }),
    [],
  )

  const slugOptions = useMemo<Record<string, CellSelectOption[]>>(() => {
    const out: Record<string, CellSelectOption[]> = {}
    if (visibleFields.some((f) => f.slug === "state")) {
      out.state = stateOptions
    }
    if (visibleFields.some((f) => f.slug === "status")) {
      out.status = projectStatuses.map((s) => ({
        value: s.slug,
        label: s.label,
        color: s.color,
      }))
    }
    return out
  }, [visibleFields, stateOptions, projectStatuses])

  function handleCustomAttributeChange(slug: string, value: unknown) {
    setCustomAttributes((current) => {
      const next = { ...current }
      if (value === null || value === undefined || value === "") {
        delete next[slug]
      } else {
        next[slug] = value
      }
      return next
    })
  }

  function handleCreatePendingMilestone(draft: MilestoneDraft) {
    const trimmed = draft.name.trim()
    if (!trimmed) return Promise.resolve()
    localMilestoneCounter += 1
    setPendingMilestones((current) => [
      ...current,
      { localId: `local-${localMilestoneCounter}`, name: trimmed, date: draft.date },
    ])
    return Promise.resolve()
  }

  function resetState() {
    setName("")
    setSummary("")
    setDescription("")
    setCustomAttributes({})
    setPendingMilestones([])
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    const trimmedName = name.trim()

    const nameSlug = projectAttributes.some((item) => item.slug === "name")
      ? "name"
      : (projectAttributes.find((item) => item.slug === "title")?.slug
        ?? projectAttributes.find((item) => item.is_required && (item.attribute_type === "text" || item.attribute_type === "long_text"))?.slug)

    const mergedCustom: Record<string, unknown> = { ...customAttributes }

    if (nameSlug && nameSlug !== "name") {
      mergedCustom[nameSlug] = trimmedName
    }

    if (summary.trim()) {
      mergedCustom.short_summary = summary.trim()
    }

    if (description.trim()) {
      if (projectAttributes.some((item) => item.slug === "description") || projectAttributes.length === 0) {
        // description пишется через top-level поле
      } else {
        const descriptionSlug = projectAttributes.find((item) => item.slug === "summary")?.slug
          ?? projectAttributes.find((item) => !item.is_required && item.attribute_type === "long_text")?.slug
        if (descriptionSlug) {
          mergedCustom[descriptionSlug] = description.trim()
        }
      }
    }

    const payload: {
      name?: string
      description?: string
      customAttributes?: Record<string, unknown>
    } = {}

    if (!nameSlug || nameSlug === "name") {
      payload.name = trimmedName
    }
    if (description.trim() && (projectAttributes.some((item) => item.slug === "description") || projectAttributes.length === 0)) {
      payload.description = description.trim()
    }
    if (Object.keys(mergedCustom).length > 0) {
      payload.customAttributes = mergedCustom
    }

    createProject.mutate(
      payload,
      {
        onSuccess: async (project) => {
          for (const milestone of pendingMilestones) {
            try {
              await createMilestone.mutateAsync({
                name: milestone.name,
                date: milestone.date || undefined,
                projectId: project.id,
              })
            } catch {
              // best-effort: project creation should still succeed
            }
          }
          resetState()
          onClose()
          if (onSuccess) {
            onSuccess(project.id)
            return
          }
          navigate(path(`/projects/${project.id}`))
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="!w-[94vw] !max-w-none sm:!w-[760px] sm:!max-w-[760px] gap-0 overflow-hidden p-0"
        overlayClassName="bg-black/30"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Новый проект</DialogTitle>

        <div className="flex items-center gap-2 border-b border-border/70 px-4 py-2.5 text-[13px]">
          <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-foreground/10 text-[11px] font-semibold text-foreground/80">
            {workspace.name?.slice(0, 2).toUpperCase() ?? "WS"}
          </div>
          <span className="truncate font-medium text-foreground/80">{workspace.name}</span>
          <HugeiconsIcon
            icon={ArrowRight01Icon}
            strokeWidth={2}
            className="size-3.5 shrink-0 text-muted-foreground/60"
          />
          <span className="text-muted-foreground">Новый проект</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="ml-auto"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex max-h-[80vh] flex-col">
          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-5">
            <Input
              ref={nameRef}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Название проекта"
              className="h-11 border-0 bg-transparent px-0 text-2xl leading-tight font-bold shadow-none ring-0 placeholder:text-muted-foreground/60 focus-visible:border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <Input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Добавьте короткое описание…"
              className="mt-1 h-7 border-0 bg-transparent px-0 text-[13px] leading-snug font-normal text-muted-foreground shadow-none ring-0 placeholder:text-muted-foreground/60 focus-visible:border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />

            {visibleFields.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {visibleFields.map((definition) => (
                  <AttributeFieldChip
                    key={definition.id}
                    definition={definition}
                    value={customAttributes[definition.slug] ?? null}
                    onCommit={(next) => handleCustomAttributeChange(definition.slug, next)}
                    statusOptions={definition.slug === "status" ? projectStatuses : undefined}
                    slugOptions={slugOptions}
                    placeholder={definition.name}
                  />
                ))}
              </div>
            ) : null}

            <div className="mt-5 border-t border-border/60 pt-4">
              <Suspense
                fallback={
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-20 rounded" />
                    <Skeleton className="h-[160px] w-full rounded-xl" />
                  </div>
                }
              >
                <RichTextMarkdownEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Описание проекта, бриф или идеи…"
                  minHeight={160}
                  className="border-0 shadow-none"
                />
              </Suspense>
            </div>

            <div className="mt-5 border-t border-border/60 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-[12.5px] font-medium text-foreground/70">Вехи</h2>
              </div>
              <MilestonesTable
                milestones={pendingMilestones.map((m) => ({
                  id: m.localId,
                  workspaceId: workspace.id,
                  objectType: "milestone",
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  name: m.name,
                  date: m.date,
                  done: false,
                  projectId: "pending",
                }))}
                onToggleDone={() => undefined}
                onSave={async (milestone, draft) => {
                  setPendingMilestones((current) =>
                    current.map((m) =>
                      m.localId === milestone.id
                        ? { ...m, name: draft.name, date: toInputDateValue(parseInputDateValue(draft.date)) }
                        : m,
                    ),
                  )
                }}
                onCreate={handleCreatePendingMilestone}
              />
            </div>
          </div>

          <DialogFooter className="px-6">
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createProject.isPending}
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              {createProject.isPending ? "Создание…" : "Создать проект"}
            </Button>
          </DialogFooter>
        </form>
        {createProject.isError ? (
          <p className="border-t border-border/70 bg-destructive/5 px-6 py-2 text-xs text-destructive">
            {createProject.error instanceof Error ? createProject.error.message : "Ошибка создания проекта"}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
